<script setup>
// Branded empty-state tile for catalog cards that have no photo yet.
// Renders a category-appropriate silhouette on an accent-tinted tile so a
// photo-less card reads as "this is a scooter / a boat tour" at a glance,
// instead of cryptic initials. Gives way to the real <img> the moment a
// photo is uploaded.
defineProps({
  kind:     { type: String, default: 'vehicle' }, // 'vehicle' | 'tour' | 'property'
  category: { type: String, default: '' }
})
</script>

<template>
  <div class="cat-ph">
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"
      class="cat-ph__icon" aria-hidden="true"
    >
      <!-- ===== Vehicles ===== -->
      <g v-if="kind === 'vehicle' && category === 'scooter'">
        <circle cx="6.5" cy="16.5" r="2.4" /><circle cx="17.5" cy="16.5" r="2.4" />
        <path d="M9 16.5h5" />
        <path d="M6.8 14.3c.3-2.5 1.8-4.3 4.2-4.3H13l1.6 3.6" />
        <path d="M14 16.5l2-6h1.4" />
        <path d="M15.3 10.5h3" />
      </g>
      <g v-else-if="kind === 'vehicle' && category === 'motorbike'">
        <circle cx="6" cy="16" r="3" /><circle cx="18" cy="16" r="3" />
        <path d="M9 16h6M8 13l2-3.5h5l2.2 4M10 9.5h4.5" />
      </g>
      <g v-else-if="kind === 'vehicle' && category === 'electric'">
        <circle cx="6.5" cy="16.5" r="2.4" /><circle cx="17.5" cy="16.5" r="2.4" />
        <path d="M9 16.5h5" />
        <path d="M6.8 14.3c.3-2.5 1.8-4.3 4.2-4.3H13l1.6 3.6" />
        <path d="M14 16.5l2-6h1.4" />
        <path d="M11.5 4.5l-2 3h2l-1 2.5" />
      </g>
      <g v-else-if="kind === 'vehicle' && category === 'suv'">
        <path d="M4 10.5l1.6-3.2h10.8L18 10.5" />
        <path d="M3 10.5h18V16H3z" />
        <circle cx="7.5" cy="16" r="2" /><circle cx="16.5" cy="16" r="2" />
        <path d="M9 10.5V8M15 10.5V8" />
      </g>
      <g v-else-if="kind === 'vehicle' && category === 'van'">
        <path d="M3 8h10v8H3z" />
        <path d="M13 11h4l3.5 3.2V16H13z" />
        <circle cx="7" cy="16.5" r="1.9" /><circle cx="17" cy="16.5" r="1.9" />
      </g>
      <g v-else-if="kind === 'vehicle' && category === 'pickup'">
        <path d="M3 10h6v3h12v3H3z" />
        <path d="M9 10l1.5-2.5H13" />
        <circle cx="7" cy="16" r="1.9" /><circle cx="17" cy="16" r="1.9" />
      </g>
      <!-- vehicle fallback (sedan / universal) -->
      <g v-else-if="kind === 'vehicle'">
        <path d="M5 11l2-4h10l2 4" />
        <path d="M3 11h18v5h-2.2M8.8 16H3z" />
        <circle cx="7" cy="16" r="2" /><circle cx="17" cy="16" r="2" />
      </g>

      <!-- ===== Tours ===== -->
      <g v-else-if="kind === 'tour' && category === 'boat_trip'">
        <path d="M3 16h18l-2.2 4H5.2L3 16z" />
        <path d="M12 4v9M12 4l5.5 4.5H12" />
      </g>
      <g v-else-if="kind === 'tour' && category === 'snorkel'">
        <rect x="4.5" y="9" width="15" height="5.5" rx="2.6" />
        <path d="M12 14.5V18a2 2 0 0 0 2 2" />
      </g>
      <g v-else-if="kind === 'tour' && category === 'island_hopping'">
        <path d="M3 19.5h18" />
        <path d="M12 19.5c0-4 0-5.5 0-7" />
        <path d="M12 12.5c-2-2-5-1.8-6 0M12 12.5c2-2 5-1.8 6 0M12 12.5c0-2.2-1.2-4-3.2-5.2M12 12.5c0-2.2 1.2-4 3.2-5.2" />
      </g>
      <g v-else-if="kind === 'tour' && category === 'adventure'">
        <path d="M3 19l6-10 4 6 2.2-3.2L21 19z" />
        <path d="M9 9l2.2 3.3" />
      </g>
      <g v-else-if="kind === 'tour' && category === 'cultural'">
        <path d="M4 20h16M6 20v-7h12v7M5 13l7-4.5L19 13M9.5 20v-4h5v4" />
      </g>
      <g v-else-if="kind === 'tour' && category === 'city'">
        <path d="M4 20V9h6v11M10 20V4h10v16" />
        <path d="M13 8h3M13 12h3M13 16h3M6.5 13h1.5M6.5 16.5h1.5" />
      </g>
      <!-- ===== Property ===== -->
      <g v-else-if="kind === 'property' && (category === 'apartment' || category === 'condo' || category === 'studio')">
        <path d="M6 21V4.5A1.5 1.5 0 0 1 7.5 3h6A1.5 1.5 0 0 1 15 4.5V21" />
        <path d="M15 9h2.5A1.5 1.5 0 0 1 19 10.5V21" />
        <path d="M3 21h18" />
        <path d="M9 7h1.5M9 11h1.5M9 15h1.5" />
      </g>
      <g v-else-if="kind === 'property'">
        <path d="M3 10.5 12 3l9 7.5" />
        <path d="M5 10v9.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10" />
        <path d="M9.5 20.5V15a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5.5" />
      </g>

      <!-- tour fallback (other) -->
      <g v-else>
        <path d="M12 21s6.5-5.2 6.5-10.5a6.5 6.5 0 1 0-13 0C5.5 15.8 12 21 12 21z" />
        <circle cx="12" cy="10.2" r="2.4" />
      </g>
    </svg>
  </div>
</template>

<style scoped>
.cat-ph {
  width: 100%; height: 100%;
  display: grid; place-items: center;
  background:
    radial-gradient(120% 120% at 50% 0%,
      color-mix(in oklch, var(--accent) 10%, var(--surfaceAlt)) 0%,
      var(--surfaceAlt) 70%);
}
.cat-ph__icon {
  width: 42%; max-width: 84px; height: auto;
  color: color-mix(in oklch, var(--accent) 50%, var(--muted));
  opacity: 0.9;
}
</style>
